import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/api/client";
import { errorMessage } from "@/api/errors";
import { useAuthStore } from "@/stores/auth";
import type { AdAccount, Organization } from "./types";

interface Props {
  onClose: () => void;
  onCreated: () => void;
}

export default function OnboardForm({ onClose, onCreated }: Props) {
  const me = useAuthStore((s) => s.user)!;
  const isSuper = me.role === "SUPER_ADMIN";
  const [metaId, setMetaId] = useState("");
  const [businessId, setBusinessId] = useState("");
  const [systemUserId, setSystemUserId] = useState("");
  const [token, setToken] = useState("");
  const [orgId, setOrgId] = useState(me.organizationId ?? "");
  const [sandbox, setSandbox] = useState(true);

  const orgsQuery = useQuery({
    queryKey: ["organizations"],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/organizations");
      if (error || !response.ok) throw error || new Error("加载失败");
      return data as unknown as Organization[];
    },
    enabled: isSuper,
  });

  const onboard = useMutation({
    mutationFn: async (payload: {
      metaAccountId: string;
      businessId?: string;
      systemUserId?: string;
      systemUserToken: string;
      organizationId?: string;
      sandbox: boolean;
    }) => {
      const { data, error, response } = await api.POST("/api/v1/ad-accounts", {
        body: payload,
      });
      if (error || !response.ok) throw error || new Error("接入失败");
      return data as unknown as AdAccount;
    },
    onSuccess: (acc) => {
      toast.success(`接入成功：${acc.name}`);
      onCreated();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  return (
    <section className="rounded-lg border border-border bg-background p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-medium">接入广告账户</h2>
        <button onClick={onClose} className="text-sm text-muted-foreground hover:text-foreground">
          取消
        </button>
      </div>

      {sandbox && (
        <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          沙盒账户在 Meta 侧没有 Business Manager / System User 概念，下方 BM ID 与 System User ID 可留空，
          只会校验 token 能否读取该账户。
        </div>
      )}

      <details className="mb-4 rounded-md border border-border bg-muted/40 text-sm">
        <summary className="cursor-pointer px-3 py-2 font-medium">
          如何获取 System User Token？（点击展开）
        </summary>
        <ol className="list-decimal space-y-3 px-8 py-3 text-muted-foreground">
          <li>
            <span className="text-foreground font-medium">进入 Business Manager 设置。</span>
            <div className="text-xs mt-1">
              打开{" "}
              <a
                href="https://business.facebook.com/settings/system-users"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                business.facebook.com/settings/system-users
              </a>
              {" "}（设置 → 用户 → 系统用户）。
            </div>
          </li>
          <li>
            <span className="text-foreground font-medium">创建系统用户并分配广告账户。</span>
            <div className="text-xs mt-1">
              点击「添加」创建一个系统用户（建议角色 *Employee*）。
              然后到该用户的「已分配资产」中加入要接入的广告账户，授权 *Manage campaigns* 权限。
            </div>
          </li>
          <li>
            <span className="text-foreground font-medium">生成永不过期的 Token。</span>
            <div className="text-xs mt-1">
              在该系统用户详情页点「生成新令牌」，勾选权限 <code className="bg-background px-1 rounded">ads_management</code>、
              <code className="bg-background px-1 rounded">ads_read</code>、
              <code className="bg-background px-1 rounded">business_management</code>，
              复制生成的 token。<strong>仅显示一次</strong>，请妥善保存。
            </div>
          </li>
          <li>
            <span className="text-foreground font-medium">填写下方表单。</span>
            <div className="text-xs mt-1">
              BM ID 在浏览器地址栏 <code className="bg-background px-1 rounded">?business_id=...</code> 参数中；
              System User ID 在系统用户详情页的 URL 末尾。
            </div>
          </li>
        </ol>
      </details>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          onboard.mutate({
            metaAccountId: metaId,
            businessId: sandbox ? undefined : businessId,
            systemUserId: sandbox ? undefined : systemUserId,
            systemUserToken: token,
            organizationId: orgId || undefined,
            sandbox,
          });
        }}
        className="grid grid-cols-1 sm:grid-cols-2 gap-3"
      >
        <label className="space-y-1.5">
          <span className="text-sm font-medium">广告账户 ID（不含 act_）</span>
          <input
            value={metaId}
            onChange={(e) => setMetaId(e.target.value)}
            placeholder="例：3609972372490726"
            required
            pattern="\d{6,20}"
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono"
          />
        </label>
        {isSuper ? (
          <label className="space-y-1.5">
            <span className="text-sm font-medium">所属公司</span>
            <select
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
              required
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            >
              <option value="">选择公司</option>
              {orgsQuery.data?.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </label>
        ) : (
          <label className="space-y-1.5">
            <span className="text-sm font-medium">所属公司</span>
            <div className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
              本公司
            </div>
          </label>
        )}

        {!sandbox && (
          <>
            <label className="space-y-1.5">
              <span className="text-sm font-medium">Business Manager ID</span>
              <input
                value={businessId}
                onChange={(e) => setBusinessId(e.target.value)}
                placeholder="例：1234567890123456"
                required
                pattern="\d{6,20}"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono"
              />
            </label>

            <label className="space-y-1.5">
              <span className="text-sm font-medium">System User ID</span>
              <input
                value={systemUserId}
                onChange={(e) => setSystemUserId(e.target.value)}
                placeholder="例：100100100100100"
                required
                pattern="\d{6,20}"
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono"
              />
            </label>
          </>
        )}

        <label className="space-y-1.5 sm:col-span-2">
          <span className="text-sm font-medium">System User Token</span>
          <textarea
            value={token}
            onChange={(e) => setToken(e.target.value)}
            required
            minLength={30}
            rows={3}
            placeholder="EAAxx... 长串（合作伙伴在 BM 系统用户处生成的永不过期令牌）"
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-xs font-mono"
          />
          <span className="text-xs text-muted-foreground">
            {sandbox
              ? "沙盒账户仅校验 token 是否可读该账户，再 AES-256-GCM 加密存储"
              : "接入时按 BM → System User → AdAccount 已分配 顺序验证后，AES-256-GCM 加密存储"}
          </span>
        </label>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={sandbox}
            onChange={(e) => setSandbox(e.target.checked)}
          />
          沙盒账户
        </label>

        <div className="sm:col-span-2 flex gap-3">
          <button
            type="submit"
            disabled={onboard.isPending}
            className="rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {onboard.isPending ? "验证并接入中..." : "接入"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border px-4 py-2 text-sm hover:bg-muted"
          >
            取消
          </button>
        </div>
      </form>
    </section>
  );
}
